import React from 'react';
import Blog from './Blog';
import Author from './models/Author';
import WordpressTag from '@gik/core/models/wordpress/WordpressTag';
import WordpressSituation from '@gik/core/models/wordpress/WordpressSituation';
import WordpressAuthor from '@gik/core/models/wordpress/WordpressAuthor';
import Article from './models/Article';
import ArticleLinkList from './models/ArticleLinkList';
import Section from './models/Section';
import WordpressConverter from './utils/WordpressConverter';
import Taxonomy from './Taxonomy';
import articleTypes from './models/ArticleTypes';
import WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import Situation from './models/Situation';
import WordpressArticleType from '../core/models/wordpress/WordpressArticleType';
import ArticleType from './models/ArticleType';
import getPreviewMode from '@gik/core/utils/PreviewMode';

interface BlogWordpressContainerProps {
  section?: string;
  taxonomySlug?: string;
  pageSlug?: string;
  tags?: WordpressTag[];
  situations?: WordpressSituation[];
  articleLinkList0?: WordpressPost[];
  articleLinkList1?: WordpressPost[];
  articleLinkList2?: WordpressPost[];
  articleLinkList3?: WordpressPost[];
  articleLinkList4?: WordpressPost[];
  articleLinkList5?: WordpressPost[];
  articleLinkList6?: WordpressPost[];
  articleLinkListTitles?: string[];
  articlesBySlug?: WordpressPost[];
  authorsBySlug?: WordpressAuthor[];
  situationsBySlug?: WordpressSituation[];
  articleTypesBySlug?: WordpressArticleType[];
}

export default function BlogWordpressContainer(props: BlogWordpressContainerProps) {
  const _getGeneralResultsPageName = (): string => {
    let pageName = null;
    if (props.taxonomySlug == Taxonomy.ContentTag) {
      const tag = props.tags.find(t => t.slug === props.pageSlug);
      if (tag) {
        pageName = tag.name;
      }
    } else if (props.taxonomySlug == Taxonomy.Situation) {
      const situation = props.situations.find(s => s.slug === props.pageSlug);
      if (situation) {
        pageName = situation.name;
      }
    } else if (props.taxonomySlug == Taxonomy.ArticleType) {
      const articleType = articleTypes.find(a => a.slug === props.pageSlug);
      if (articleType) {
        pageName = articleType.name;
      }
    }
    pageName = pageName || props.pageSlug.toString().replace(/-/g, ' ');
    return pageName
      .split(' ')
      .map(word => word[0].toUpperCase() + word.substring(1))
      .join(' ');
  };

  const previewMode = getPreviewMode();

  let articleLinkLists: ArticleLinkList[] = [];
  if (props.articleLinkList0) {
    articleLinkLists = [
      props.articleLinkList0,
      props.articleLinkList1 || [],
      props.articleLinkList2 || [],
      props.articleLinkList3 || [],
      props.articleLinkList4 || [],
      props.articleLinkList5 || [],
      props.articleLinkList6 || [],
    ]
      .map(list => (list.length !== undefined ? list : Object.values(list)))
      .map((list, index) => ({
        articleLinks: list
          .map(post => WordpressConverter.convertPost(post))
          .filter(article => !article.isPreviewOnly || article.isPreviewOnly === previewMode),
        title:
          props.articleLinkListTitles && index < props.articleLinkListTitles.length
            ? props.articleLinkListTitles[index]
            : 'Unknown title',
      }));
  }

  const wordpressPost = props.articlesBySlug ? props.articlesBySlug[0] : null;
  const wordpressAuthor = props.authorsBySlug ? props.authorsBySlug[0] : null;
  const wordpressSituation = props.situationsBySlug ? props.situationsBySlug[0] : null;
  const wordpressArticleType = props.articleTypesBySlug ? props.articleTypesBySlug[0] : null;

  const article: Article | null = wordpressPost ? WordpressConverter.convertPost(wordpressPost) : null;
  const author: Author | null = wordpressAuthor ? WordpressConverter.convertAuthor(wordpressAuthor) : null;
  const situation: Situation | null = wordpressSituation
    ? WordpressConverter.convertSituation(wordpressSituation)
    : null;
  const articleType: ArticleType | null = wordpressArticleType
    ? WordpressConverter.convertArticleType(wordpressArticleType)
    : null;

  let tags: Section[] = [];
  if (props.tags) {
    const allTags = props.tags.map(WordpressConverter.convertTag).filter(tag => tag.postCount > 0);
    tags = [
      {
        titleCategory: { name: 'Popular Tags', id: '', slug: '', taxonomy: '' },
        categories: allTags
          .slice()
          .sort((a, b) => b.postCount - a.postCount)
          .slice(0, 10),
      },
      {
        titleCategory: { name: 'All Tags', id: '', slug: '', taxonomy: '' },
        categories: allTags,
      },
    ];
  }
  let situations: Section[] = [];
  if (props.situations) {
    const allSituations = props.situations.map(WordpressConverter.convertSituation);
    const parents = allSituations.filter(parent => parent.parentId === '0');

    situations = parents
      .map(parent => ({
        titleCategory: parent,
        categories: allSituations.filter(child => child.parentId == parent.id),
      }))
      .filter(parent => parent.categories.length > 0); // remove empty
  }

  return (
    <Blog
      section={props.section}
      taxonomySlug={props.taxonomySlug}
      pageSlug={props.pageSlug}
      articleLinkLists={articleLinkLists}
      tags={tags}
      situations={situations}
      article={article}
      author={author}
      getGeneralResultsPageName={_getGeneralResultsPageName}
      situation={situation}
      articleType={articleType}
    />
  );
}
